import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRightIcon, FileTextIcon, GitCompareIcon } from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { RESOURCE_CACHE_HEADERS, resourcesIndexHead } from '../../lib/resource-head';
import { loadResourcesIndex } from '../../lib/resource-loaders';
import type { ToolContent } from '../../lib/tool-content';
import type { ComparisonContent } from '../../lib/comparison-content';

const title = 'Risk of Bias and Quality Appraisal Tools | CoRATES';
const description =
  'Guides to the appraisal tools CoRATES supports: RoB 2 for randomized trials, ROBINS-I for non-randomized studies, and AMSTAR 2 for systematic reviews, plus comparisons to help you choose between them.';

export const Route = createFileRoute('/resources/')({
  headers: () => RESOURCE_CACHE_HEADERS,
  loader: loadResourcesIndex,
  head: ({ loaderData }) =>
    loaderData ?
      resourcesIndexHead(title, description, loaderData.pages, loaderData.dateModified)
    : {},
  component: ResourcesPage,
});

function ToolCard({ tool }: { tool: Pick<ToolContent, 'slug' | 'name' | 'summary'> }) {
  return (
    <Link
      to={`/resources/${tool.slug}` as string}
      className='group flex flex-col rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-blue-300 hover:shadow-lg'
    >
      <div className='mb-4 flex size-12 items-center justify-center rounded-lg bg-blue-100'>
        <FileTextIcon className='size-6 text-blue-600' />
      </div>
      <h2 className='mb-2 text-xl font-semibold text-gray-900'>{tool.name}</h2>
      <p className='mb-4 flex-1 text-sm text-gray-600'>{tool.summary}</p>
      <div className='flex items-center gap-2 text-sm font-medium text-blue-600 group-hover:text-blue-700'>
        Learn more
        <ArrowRightIcon className='size-4 transition-transform group-hover:translate-x-1' />
      </div>
    </Link>
  );
}

function ComparisonCard({
  comparison,
}: {
  comparison: Pick<ComparisonContent, 'slug' | 'title' | 'metaDescription'>;
}) {
  return (
    <Link
      to={`/resources/${comparison.slug}` as string}
      className='group flex flex-col rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-blue-300 hover:shadow-lg'
    >
      <div className='mb-4 flex size-12 items-center justify-center rounded-lg bg-blue-100'>
        <GitCompareIcon className='size-6 text-blue-600' />
      </div>
      <h3 className='mb-2 text-lg font-semibold text-gray-900'>{comparison.title}</h3>
      <p className='mb-4 flex-1 text-sm text-gray-600'>{comparison.metaDescription}</p>
      <div className='flex items-center gap-2 text-sm font-medium text-blue-600 group-hover:text-blue-700'>
        Read the comparison
        <ArrowRightIcon className='size-4 transition-transform group-hover:translate-x-1' />
      </div>
    </Link>
  );
}

function ResourcesPage() {
  const { tools, comparisons } = Route.useLoaderData();

  return (
    <div className='flex min-h-screen flex-col'>
      <Navbar />

      <main className='flex-1 py-12'>
        <div className='mx-auto max-w-6xl px-6'>
          <h1 className='mb-2 text-4xl font-bold text-gray-900'>
            Risk of Bias and Quality Appraisal Tools
          </h1>
          <p className='mb-10 text-lg text-gray-500'>
            Guidance on the appraisal instruments used in systematic reviews and evidence synthesis
          </p>

          <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
            {tools.map(tool => (
              <ToolCard key={tool.id} tool={tool} />
            ))}
          </div>

          <h2 className='mt-14 mb-2 text-2xl font-bold text-gray-900'>Choosing between tools</h2>
          <p className='mb-6 text-gray-500'>
            Which tool fits a given study design, what separates the alternatives, and what changed
            between versions
          </p>
          <div className='grid gap-6 sm:grid-cols-2'>
            {comparisons.map(comparison => (
              <ComparisonCard key={comparison.slug} comparison={comparison} />
            ))}
          </div>

          <div className='mt-12 rounded-lg bg-gray-50 p-6'>
            <h2 className='mb-3 text-lg font-semibold text-gray-900'>About these tools</h2>
            <p className='text-gray-600'>
              CoRATES supports multiple evidence appraisal tools used in systematic reviews and
              research synthesis. Each tool is designed for specific study types and provides
              structured guidance for assessing methodological quality or risk of bias. Select a
              tool above to learn more about its purpose, scoring methodology, and access official
              documentation.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
